import { useEffect, useRef, useState } from "react";
import type * as Y from "yjs";

export type AutosaveStatus = {
  state: "idle" | "saving" | "saved";
  lastSavedAt: Date | null;
};

const SAVING_WINDOW_MS = 350;

/**
 * Observerar Y.Doc-updates och exponerar en enkel statusmaskin för UI:
 *   idle   — ingen ändring ännu (färsk dokument)
 *   saving — användaren ändrade precis något, y-indexeddb skriver
 *   saved  — ingen pending ändring, senast sparad vid `lastSavedAt`
 *
 * y-indexeddb persisterar synkront varje update till IndexedDB. Vi använder
 * ett debounce-fönster på 350 ms för att visa "Sparar…" som feedback utan att
 * flicker:a — så snart ingen ny ändring trillar in inom fönstret antas
 * sparningen vara klar.
 */
export function useAutosaveStatus(doc: Y.Doc): AutosaveStatus {
  const [status, setStatus] = useState<AutosaveStatus>({
    state: "idle",
    lastSavedAt: null,
  });
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const handler = (_update: Uint8Array, origin: unknown) => {
      // Ignorera egna initial-writes som kommer från y-indexeddb self-load —
      // de har origin från persistence-providern och ska inte räknas som
      // en user-change.
      if (origin && typeof origin === "object" && origin.constructor?.name === "IndexeddbPersistence") {
        return;
      }
      setStatus((prev) => ({ ...prev, state: "saving" }));
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setStatus({ state: "saved", lastSavedAt: new Date() });
        timerRef.current = null;
      }, SAVING_WINDOW_MS);
    };

    doc.on("update", handler);
    return () => {
      doc.off("update", handler);
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [doc]);

  return status;
}
